import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Navigation from '../../components/Navigation/Navigation'
import CollectionCard from '../../components/CollectionCard/CollectionCard'
import Sidebar from '../../components/Sidebar/Sidebar'
import AddItemMenu from '../../components/AddItemMenu/AddItemMenu'
import { useCollections } from '../../hooks/useCollections'
import { useBreakpoints } from '../../hooks/useBreakpoints'
import styles from './HomePage.module.css'
import layoutStyles from '../../styles/layout.module.css'
import buttonStyles from '../../styles/buttons.module.css'
import collectionCardStyles from '../../components/CollectionCard/CollectionCard.module.css'

export default function HomePage() {
  const navigate = useNavigate()
  const searchInputRef = useRef<HTMLInputElement>(null)
  const addItemButtonRef = useRef<HTMLButtonElement>(null)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0
  const shortcut = isMac ? '(⌘ + K)' : '(Ctrl + K)'
  
  const { data: collectionsData, isLoading, error } = useCollections()
  const { screenSize } = useBreakpoints()

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Command+K (Mac) or Ctrl+K (Windows/Linux)
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault()
        searchInputRef.current?.focus()
        return
      }

      // Check if down arrow key is pressed
      if (event.key === 'ArrowDown') {
        const activeElement = document.activeElement
        
        // Check if user is focused on any interactive element
        const isFocusedOnInteractive = activeElement && (
          activeElement.tagName === 'INPUT' ||
          activeElement.tagName === 'BUTTON' ||
          activeElement.tagName === 'TEXTAREA' ||
          activeElement.tagName === 'SELECT' ||
          activeElement.tagName === 'A' ||
          activeElement.getAttribute('contenteditable') === 'true' ||
          activeElement.getAttribute('tabindex') !== null
        )
        
        // Check if no collection card or its sub-elements are focused
        const isCollectionCardFocused = activeElement?.closest('.collection-card')
        
        if (!isFocusedOnInteractive && !isCollectionCardFocused) {
          event.preventDefault()
          // Navigate to recently added page
          navigate('/recently-added')
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [navigate])

  const handleAddLink = (url: string) => {
    console.log('Adding link:', url)
  }

  const handleUploadFile = () => {
    console.log('Uploading file')
  }
  
  return (
    <div className={layoutStyles.mainLayout}>
      <Sidebar activeItem="home" />
      
      <div className={`${layoutStyles.mainContentWrapper} ${screenSize === 'phone' ? layoutStyles.mainContentWrapperPhone : ''}`}>
        <Navigation />
        
        {/* Search Section */}
        <section className={`${styles.heroSection} ${screenSize === 'phone' ? styles.heroSectionPhone : ''}`}>
          <div className={`${styles.heroContainer} ${screenSize === 'phone' ? styles.heroContainerPhone : ''}`}>
            <div className={`${styles.searchContainer} ${screenSize === 'tablet' ? styles.searchContainerTablet : ''} ${screenSize === 'phone' ? styles.searchContainerPhone : ''}`}>
              <div className={`${styles.searchInputWrapper} ${screenSize === 'phone' ? styles.searchInputWrapperPhone : ''}`}>
                <svg className={styles.searchIcon} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M17.5 17.5L12.5 12.5M14.1667 8.33333C14.1667 11.555 11.555 14.1667 8.33333 14.1667C5.11167 14.1667 2.5 11.555 2.5 8.33333C2.5 5.11167 5.11167 2.5 8.33333 2.5C11.555 2.5 14.1667 5.11167 14.1667 8.33333Z" stroke="#303030" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <input 
                  ref={searchInputRef}
                  type="text" 
                  placeholder={screenSize === 'phone' || screenSize === 'tablet' ? 'Search or ask anything' : `Search or ask anything ${shortcut}`}
                  className={`${styles.searchInput} ${screenSize === 'phone' ? styles.searchInputPhone : ''}`}
                />
              </div>
              <div style={{ position: 'relative' }}>
                <button 
                  ref={addItemButtonRef}
                  className={`${buttonStyles.addItemButton} ${screenSize === 'phone' ? buttonStyles.addItemButtonPhone : ''}`}
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  aria-expanded={isMenuOpen}
                  aria-haspopup="dialog"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 3.33334V12.6667M3.33334 8H12.6667" stroke="#333333" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {screenSize === 'phone' ? '' : 'Add Item'}
                </button>
                <AddItemMenu
                  isOpen={isMenuOpen}
                  onClose={() => setIsMenuOpen(false)}
                  anchorElement={addItemButtonRef.current}
                  onAddLink={handleAddLink}
                  onUploadFile={handleUploadFile}
                />
              </div>
            </div>
          </div>
        </section>

        <main className={layoutStyles.mainContent}>
          <div className={layoutStyles.container}>
            {isLoading && (
              <div className={styles.loadingSpinner}>
                <div className={styles.spinner}></div>
                <p>Loading collections...</p>
              </div>
            )}
            
            {error && (
              <div className={styles.errorMessage}>
                <p>Failed to load collections. Please try again.</p>
              </div>
            )}
            
            {collectionsData && (
              <div className={`${styles.collectionsGrid} ${styles[`collectionsGrid${screenSize.charAt(0).toUpperCase() + screenSize.slice(1)}`]}`}>
                {collectionsData.collections.map(collection => (
                  <CollectionCard
                    key={collection.id}
                    id={collection.id}
                    title={collection.title}
                    thumbnailItems={collection.thumbnails}
                    className={screenSize === 'phone' ? collectionCardStyles.collectionCardPhone : ''}
                  />
                ))}
              </div>
            )}
          </div>
          <button className={buttonStyles.scrollDownButton} onClick={() => {
            window.scrollTo({
              top: document.body.scrollHeight,
              behavior: 'smooth'
            });
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 10L12 15L17 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </main>
      </div>
    </div>
  )
}